import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Stack,
  Tooltip,
  IconButton,
  Snackbar,
  Alert,
  TextField,
  Autocomplete,
  Button,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import moment from "moment";
import { useAppMeta, useAuth, useTablePagination } from "hooks";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import {
  useLazyGetBranchSalesReportQuery,
  useDownloadBranchSalesReportExcelMutation,
} from "store/services/reportApi";
import {
  useLazyGetDDLBranchListQuery,
  useLazyGetBranchByRegionDDLQuery,
} from "store/services/branchApi";
import { useLazyGetDdlRegionListQuery } from "store/services/LocationsApi/regionApi";
import BaseSelect from "components/Shared/Base/BaseSelect";
import { Download, Visibility } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";


const BranchReport = () => {
  const { isVendor, vendorId, viewEntity } = useAuth();
  const { reset } = useForm({ defaultValues: { selectedPrices: [] } });
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();
  const [sortBy, setSortBy] = useState("branchName:1");
  const [branch, setBranch] = useState(searchParams.get("branch") || "all");
  const [region, setRegion] = useState(searchParams.get("region") || "all");
  const [timeRange, setTimeRange] = useState(
    searchParams.get("timeRange") || "last7days"
  );
  const [customFromDate, setCustomFromDate] = useState(
    searchParams.get("fromDate") ? new Date(searchParams.get("fromDate")) : null
  );
  const [customToDate, setCustomToDate] = useState(
    searchParams.get("toDate") ? new Date(searchParams.get("toDate")) : null
  );
  // These are the dates actually used for fetching — only updated on "Apply" for custom range
  const [appliedFromDate, setAppliedFromDate] = useState(customFromDate);
  const [appliedToDate, setAppliedToDate] = useState(customToDate);
  const [errorMessage, setErrorMessage] = useState("");

  const breadcrumbs = [
    { title: "Reports", path: "/app/crm/reports/list" },
    { title: "Branch Report" },
  ];

  const [
    fetchBranchSalesReport,
    {
      data: branchReportData,
      isLoading: isBranchLoading,
      isFetching: isBranchFetching,
      error: branchError,
    },
  ] = useLazyGetBranchSalesReportQuery();
  const [downloadExcel] = useDownloadBranchSalesReportExcelMutation();

  const [fetchBranches, { data: branchesData, isLoading: isBranchesLoading }] =
    useLazyGetDDLBranchListQuery();
  const [
    fetchBranchesByRegion,
    {
      data: branchesByRegionData,
      isLoading: isBranchesByRegionLoading,
      error: branchesByRegionError,
    },
  ] = useLazyGetBranchByRegionDDLQuery();
  const [fetchRegions, { data: regionsData, isLoading: isRegionsLoading }] =
    useLazyGetDdlRegionListQuery();

  useEffect(() => {
    const accountId = viewEntity?.entity?.account?._id;
    const entityId = viewEntity?.entity?._id;

    if (!accountId || !entityId) return;

    fetchRegions({ account: accountId, entity: entityId });
    if (region === "all") {
      fetchBranches({ account: accountId, entity: entityId });
    } else if (region && typeof region === "string") {
      fetchBranchesByRegion({
        regionId: region,
        account: accountId,
        entity: entityId,
      });
    } else {
      setErrorMessage("Invalid region selection. Please try again.");
    }
  }, [fetchBranches, fetchBranchesByRegion, fetchRegions, viewEntity, region]);

  const regionOptions = [
    { label: "All Provinces", value: "all" },
    ...(regionsData?.map((region) => ({
      label: region.label || region.name,
      value: region.value || region._id,
    })) || []),
  ];

  const branchOptions = [
    { label: "All Branches", value: "all" },
    ...(region === "all"
      ? branchesData?.map((branch) => ({
        label: branch.label || branch.name,
        value: branch.value || branch._id,
      })) || []
      : branchesByRegionData?.data?.map((branch) => ({
        label: branch.name,
        value: branch._id,
      })) || []),
  ];

  const getDateRange = useCallback((range, from, to) => {
    const today = moment().startOf("day");
    switch (range) {
      case "today":
        return {
          fromDate: today.format("MM-DD-YYYY"),
          toDate: today.endOf("day").format("MM-DD-YYYY"),
        };
      case "last7days":
        return {
          fromDate: moment()
            .subtract(6, "days")
            .startOf("day")
            .format("MM-DD-YYYY"),
          toDate: today.endOf("day").format("MM-DD-YYYY"),
        };
      case "oneMonth":
        return {
          fromDate: moment()
            .subtract(1, "month")
            .startOf("day")
            .format("MM-DD-YYYY"),
          toDate: today.endOf("day").format("MM-DD-YYYY"),
        };
      case "custom":
        if (from && to && moment(from).isValid() && moment(to).isValid()) {
          if (moment(to).diff(moment(from), "months", true) > 3) {
            setErrorMessage("Date range cannot exceed 3 months.");
            return { fromDate: null, toDate: null };
          }
          return {
            fromDate: moment(from).format("MM-DD-YYYY"),
            toDate: moment(to).format("MM-DD-YYYY"),
          };
        }
        setErrorMessage(
          "Invalid date range. Ensure 'To' date is after 'From' date."
        );
        return { fromDate: null, toDate: null };
      case "all":
      default:
        return { fromDate: null, toDate: null };
    }
  }, []);

  const fetchReportData = useCallback(() => {
    // For custom range, don't hit the API until both dates are applied via the Apply button
    if (timeRange === "custom" && (!appliedFromDate || !appliedToDate)) {
      return;
    }
    const [field, direction] = sortBy.split(":");
    const sortedBy =
      field === "branchName" || field === "total" ? field : "branchName";
    const sortDirection =
      direction === "1" || direction === "-1" ? direction : "1";
    const { fromDate, toDate } = getDateRange(
      timeRange,
      appliedFromDate,
      appliedToDate
    );
    const newParams = new URLSearchParams(searchParams);
    newParams.set("timeRange", timeRange);
    newParams.set("page", currentPage.toString());
    newParams.set("limit", perPage.toString());
    if (fromDate && fromDate !== "null") newParams.set("fromDate", fromDate);
    else newParams.delete("fromDate");
    if (toDate && toDate !== "null") newParams.set("toDate", toDate);
    else newParams.delete("toDate");
    if (branch !== "all") newParams.set("branch", branch);
    else newParams.delete("branch");
    if (region !== "all") newParams.set("region", region);
    else newParams.delete("region");

    setSearchParams(newParams, { replace: true });

    const apiParams = {
      page: currentPage,
      limit: perPage,
      ...(isVendor && { vendorId }),
      account: viewEntity?.entity?.account?._id,
      entity: viewEntity?.entity?._id,
      branch: branch !== "all" ? branch : undefined,
      region: region !== "all" ? region : undefined,
      fromDate: fromDate === "null" || !fromDate ? undefined : fromDate,
      toDate: toDate === "null" || !toDate ? undefined : toDate,
      sortedBy,
      sortDirection,
    };

    fetchBranchSalesReport({ params: apiParams }).catch((err) => {
      setErrorMessage(
        err?.data?.message || "Failed to fetch branch sales report"
      );
      console.error("Branch Sales Report Error:", err);
    });
  }, [
    sortBy,
    getDateRange,
    timeRange,
    appliedFromDate,
    appliedToDate,
    branch,
    region,
    setSearchParams,
    currentPage,
    perPage,
    isVendor,
    vendorId,
    viewEntity,
    fetchBranchSalesReport,
    searchParams,
  ]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  useEffect(() => {
    if (branchesByRegionError) {
      setErrorMessage("Failed to fetch branches for the selected region.");
    }
    if (branchError) {
      setErrorMessage("Failed to fetch branch sales report.");
    }
  }, [branchesByRegionError, branchError]);

  //   const clearAllFilters = () => {
  //     setSearchParams({ timeRange: "last7days" });
  //     setTimeRange("last7days");
  //     setBranch("all");
  //     setRegion("all");
  //     setCustomFromDate(null);
  //     setCustomToDate(null);
  //     setSortBy("branchName:1");
  //     reset({ selectedPrices: [] });
  //   };

  const handleDownloadExcel = async () => {
    const [field, direction] = sortBy.split(":");
    const sortedBy =
      field === "branchName" || field === "total" ? field : "branchName";
    const sortDirection =
      direction === "1" || direction === "-1" ? direction : "1";

    const { fromDate, toDate } = getDateRange(
      timeRange,
      customFromDate,
      customToDate
    );

    const apiParams = {
      fromDate: fromDate === "null" || !fromDate ? undefined : fromDate,
      toDate: toDate === "null" || !toDate ? undefined : toDate,
      ...(isVendor && { vendorId }),
      account: viewEntity?.entity?.account?._id,
      entity: viewEntity?.entity?._id,
      branch: branch !== "all" ? branch : undefined,
      region: region !== "all" ? region : undefined,
      sortedBy,
      sortDirection,
      page: 1,
      limit: 10000,
    };

    try {
      const response = await downloadExcel({ params: apiParams }).unwrap();
      if (!response.blob) {
        throw new Error("No data returned from server");
      }

      const contentType =
        response.blob.type ||
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      if (!contentType.includes("spreadsheetml.sheet")) {
        throw new Error("Invalid content type received from server");
      }

      const url = window.URL.createObjectURL(response.blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Branch_Report_${moment().format("YYYYMMDD")}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setErrorMessage(
        error?.data?.message ||
        "Failed to download Excel file. Check filters or server status."
      );
      console.error("Failed to download Excel:", error);
    }
  };
  const navigate = useNavigate();

  const columns = [
    {
      name: "Branch Name",
      selector: (row) => row.branchName,
      cell: (row) => row.branchName || "N/A",
    },
    {
      name: "Province",
      selector: (row) => row.regionName,
      cell: (row) => row.regionName || "N/A",
    },

    {
      name: "Paid",
      selector: (row) => row.paid,
      cell: (row) => `Rs. ${row.paid?.toFixed(2) || "0.00"}`,
    },
    {
      name: "Not Paid",
      selector: (row) => row.notPaid,
      cell: (row) => `Rs. ${row.notPaid?.toFixed(2) || "0.00"}`,
    },
    {
      name: "Collected",
      selector: (row) => row.collected,
      cell: (row) => `Rs. ${row.collected?.toFixed(2) || "0.00"}`,
    },
    {
      name: "Total Sales",
      selector: (row) => row.total,
      cell: (row) => `Rs. ${row.total?.toFixed(2) || "0.00"}`,
    },
    {
      name: "Action",
      cell: (row) => (
        <IconButton
          color="primary"
          size="small"
          onClick={() => {
            const params = new URLSearchParams();
            params.set("branch", row._id);
            params.set("page", "1");
            navigate(`/app/crm/reports/shipment-pricing?${params.toString()}`);
          }}
        >
          <Visibility />
        </IconButton>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "150px",
    },
  ];
  const summaryMetrics = [
    {
      label: "Total Paid",
      value: branchReportData?.totalPaid?.toFixed(2) || "0.00",
    },
    {
      label: "Total Not Paid",
      value: branchReportData?.totalNotPaid?.toFixed(2) || "0.00",
    },
    {
      label: "Total Collected",
      value: branchReportData?.totalCollected?.toFixed(2) || "0.00",
    },
    {
      label: (
        <Typography color="primary" fontWeight={700} variant="subtitle1">
          Total Sales
        </Typography>
      ),
      value: branchReportData?.totalAmount?.toFixed(2) || "0.00",
    },
  ];

  const getRangeDisplay = () => {
    switch (timeRange) {
      case "today":
        return "Today";
      case "last7days":
        return "Last 7 Days";
      case "oneMonth":
        return "One Month";
      case "custom":
        if (customFromDate && customToDate) {
          return `Custom (${moment(customFromDate).format(
            "MM/DD/YYYY"
          )} - ${moment(customToDate).format("MM/DD/YYYY")})`;
        }
        return "Custom (Invalid Dates)";
      case "all":
      default:
        return "All";
    }
  };

  useAppMeta("Branch Report");
  const isCustomRangeValid =
    customFromDate &&
    customToDate &&
    moment(customFromDate).isValid() &&
    moment(customToDate).isValid() &&
    moment(customToDate).isAfter(moment(customFromDate)) &&
    moment(customToDate).diff(moment(customFromDate), "months", true) <= 3;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <BreadcrumbLayout breadcrumbs={breadcrumbs}>
        <BreadcrumbLayout.Error error={branchError} />
        <BreadcrumbLayout.Paper>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "space-between", // Title left, filters right
              gap: 2,
              alignItems: "center",
              px: 2,
              pt: 2,
            }}
          >
            {/* Title */}
            <Typography variant="h6" fontWeight="bold" sx={{ flexShrink: 0 }}>
              Branch Report
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "flex-end",
                gap: 2,
                alignItems: "center",
              }}
            >
              <BaseSelect
                label="Range"
                value={timeRange}
                onChange={(e) => {
                  setTimeRange(e.target.value);
                  if (e.target.value !== "custom") {
                    setCustomFromDate(null);
                    setCustomToDate(null);
                    setAppliedFromDate(null);
                    setAppliedToDate(null);
                  }
                }}
                options={[
                  { label: "Today", value: "today" },
                  { label: "Last 7 Days", value: "last7days" },
                  { label: "One Month", value: "oneMonth" },
                  { label: "Custom", value: "custom" },
                ]}
                getOptionLabel={(opt) => opt.label}
                getOptionValue={(opt) => opt.value}
                size="small"
                sx={{ minWidth: 140 }}
              />

              {timeRange === "custom" && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <DatePicker
                    label="From"
                    value={customFromDate}
                    onChange={(newValue) => {
                      setCustomFromDate(newValue);
                      if (
                        customToDate &&
                        moment(customToDate).diff(
                          moment(newValue),
                          "months",
                          true
                        ) > 3
                      ) {
                        setCustomToDate(
                          moment(newValue).add(3, "months").toDate()
                        );
                        setErrorMessage(
                          "Date range adjusted to maximum 3 months."
                        );
                      }
                    }}
                    renderInput={(params) => (
                      <TextField {...params} size="small" sx={{ width: 140 }} />
                    )}
                    maxDate={customToDate || new Date()}
                    disableFuture
                  />
                  <DatePicker
                    label="To"
                    value={customToDate}
                    onChange={(newValue) => {
                      setCustomToDate(newValue);
                      if (
                        customFromDate &&
                        moment(newValue).diff(
                          moment(customFromDate),
                          "months",
                          true
                        ) > 3
                      ) {
                        setCustomFromDate(
                          moment(newValue).subtract(3, "months").toDate()
                        );
                        setErrorMessage(
                          "Date range adjusted to maximum 3 months."
                        );
                      }
                    }}
                    renderInput={(params) => (
                      <TextField {...params} size="small" sx={{ width: 140 }} />
                    )}
                    minDate={customFromDate}
                    maxDate={new Date()}
                    disableFuture
                  />
                  <Tooltip
                    title={
                      isCustomRangeValid
                        ? "Apply date range"
                        : "Select a valid range (max 3 months)"
                    }
                  >
                    <span>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={!isCustomRangeValid}
                        onClick={() => {
                          setAppliedFromDate(customFromDate);
                          setAppliedToDate(customToDate);
                        }}
                      >
                        Apply
                      </Button>
                    </span>
                  </Tooltip>
                </Stack>
              )}
              <Autocomplete
                size="small"
                options={regionOptions}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                value={
                  regionOptions.find((opt) => opt.value === region) || {
                    value: "all",
                    label: "All Provinces",
                  }
                }
                onChange={(e, newValue) => {
                  const newRegion = newValue?.value || "all";
                  setRegion(newRegion);
                  setBranch("all");
                }}
                disableClearable
                disabled={isRegionsLoading}
                sx={{ minWidth: 200 }}
                renderInput={(params) => (
                  <TextField {...params} label="Province" />
                )}
              />
              <Autocomplete
                size="small"
                options={branchOptions}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                value={
                  branchOptions.find((opt) => opt.value === branch) || {
                    value: "all",
                    label: "All Branches",
                  }
                }
                onChange={(e, newValue) => setBranch(newValue?.value || "all")}
                disableClearable
                disabled={isBranchesLoading || isBranchesByRegionLoading}
                sx={{ minWidth: 200 }}
                renderInput={(params) => (
                  <TextField {...params} label="Branch" />
                )}
              />
              <BaseSelect
                label="Sort By"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                options={[
                  { label: "Branch Name (Asc)", value: "branchName:1" },
                  { label: "Branch Name (Desc)", value: "branchName:-1" },
                  { label: "Total (Asc)", value: "total:1" },
                  { label: "Total (Desc)", value: "total:-1" },
                ]}
                getOptionLabel={(opt) => opt.label}
                getOptionValue={(opt) => opt.value}
                size="small"
                sx={{ minWidth: 230 }}
              />
              <Tooltip title="Download Excel">
                <span>
                  <IconButton
                    onClick={handleDownloadExcel}
                    color="primary"
                    disabled={
                      isBranchLoading ||
                      isBranchFetching ||
                      !branchReportData?.data?.length
                    }
                  >
                    <Download />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>

          {branchReportData?.totalAmount !== undefined && (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "flex-end",
                gap: 2,
                alignItems: "center",
                px: 2,
                pt: 2,
              }}
            >
              <Stack direction="row" spacing={3} sx={{ flexWrap: "wrap" }}>
                <Box>
                  <Typography variant="body2" fontWeight="normal">
                    {`${getRangeDisplay()} Branch Summary:`}
                  </Typography>
                </Box>
                {summaryMetrics.map((metric) => (
                  <Box key={metric.label}>
                    <Typography variant="body2" color="text.secondary">
                      {metric.label}
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      Rs. {metric.value}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {isBranchLoading ||
            isBranchFetching ||
            isBranchesLoading ||
            isBranchesByRegionLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : branchReportData?.data?.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No branches available. Check branch data in the database.
              </Typography>
            </Box>
          ) : (
            <AppTable
              columns={columns}
              data={branchReportData?.data || []}
              progressPending={isBranchLoading || isBranchFetching}
              paginationServer
              paginationPerPage={perPage}
              paginationTotalRows={branchReportData?.totalItems ?? 0}
              paginationDefaultPage={currentPage}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handlePerRowsChange}
            //   subHeader
            />
          )}

          <Snackbar
            open={!!errorMessage}
            autoHideDuration={6000}
            onClose={() => setErrorMessage("")}
          >
            <Alert severity="error" onClose={() => setErrorMessage("")}>
              {errorMessage}
            </Alert>
          </Snackbar>
        </BreadcrumbLayout.Paper>
      </BreadcrumbLayout>
    </LocalizationProvider>
  );
};

export default BranchReport;
